'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'

export default function useModal<P extends {} = {}>(modal: string) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const isOpen = searchParams.get('modal') === modal

  const open: (options: P) => void = useCallback(
    (options) => {
      const params = new URLSearchParams(searchParams.toString())

      params.set('modal', modal)

      if (options) {
        for (const key in options) {
          params.set(key, String(options[key]))
        }
      }

      window.history.pushState(null, '', `?${params.toString()}`)
    },
    [modal, searchParams]
  )

  const update: (options: Partial<P>) => void = useCallback(
    (options) => {
      if (!isOpen) {
        return
      }

      const params = new URLSearchParams(searchParams.toString())

      if (options) {
        for (const key in options) {
          params.set(key, String(options[key]))
        }
      }

      window.history.replaceState(null, '', `?${params.toString()}`)
    },
    [isOpen, searchParams]
  )

  const close = useCallback(() => {
    if (!isOpen) {
      return
    }

    window.history.replaceState(null, '', pathname)
  }, [isOpen, pathname])

  return { isOpen, open, update, close }
}
